import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';

import { buildScriptElements, CustomScriptType, getCustomScriptType, ScriptInjection, ScriptPosition } from '@/helpers/scripts';
import { CompanyAccount } from '@/models/companyModels';
import { CustomScriptsType } from '@/models/customizationModels';
import { selectCustomScriptsCustomization } from '@/redux/customizations/reducers';
import { useAppSelector } from '@/redux/hooks';

/**
 * Custom hook for injecting page-specific scripts
 * @param companyAccount - Optional company account with customizations
 * @param nonce - Optional CSP nonce for script elements
 */
export default function useCustomScripts(companyAccount?: CompanyAccount, nonce?: string): void {
  const { pathname } = useRouter();
  const customizations = useAppSelector(selectCustomScriptsCustomization);
  const previousPath = useRef(pathname);

  const scriptType = useMemo(() => getCustomScriptType(pathname), [pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!scriptType) return;

    const scriptsToInject = getScripts(scriptType, customizations);
    if (!scriptsToInject.head && !scriptsToInject.body) return;

    Object.keys(scriptsToInject).forEach((position) => {
      const dataId = getScriptDataId(position as ScriptPosition, scriptType);
      const isSamePath = previousPath.current === pathname;
      const existingScripts = document.querySelectorAll(`[${dataId}]`);

      if (existingScripts.length > 0 && isSamePath) return; // Already injected for the same path

      if (existingScripts.length > 0 && !isSamePath) {
        existingScripts.forEach((script) => script.remove()); // Remove old script if path changed
      }

      const content = scriptsToInject[position as ScriptPosition];
      const scriptsEl = content ? buildScriptElements(dataId, content, nonce) : [];
      if (!scriptsEl.length) return;

      if (position === 'head') {
        document.head.append(...scriptsEl);
      } else {
        document.body.append(...scriptsEl);
      }
    });

    // Set previous path to current path
    previousPath.current = pathname;
  }, [scriptType, customizations, nonce, pathname]);
}

export const getScriptDataId = (position: ScriptPosition, type: CustomScriptType) => {
  return `data-custom-script-${type}-${position}`;
};

const getScripts = (scriptType: CustomScriptType, customizations: CustomScriptsType): ScriptInjection => {
  const headScriptKey = `${scriptType}_head_scripts` as keyof typeof customizations;
  const bodyScriptKey = `${scriptType}_body_scripts` as keyof typeof customizations;

  return {
    head: customizations[headScriptKey] as string | undefined,
    body: customizations[bodyScriptKey] as string | undefined,
  };
};
